import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import { notifySignedIn, notifySignedOut } from "@/lib/api/session"
import { clearToken, saveToken } from "@/lib/api/token"
import type { ApiResource } from "@/lib/api/types"
import type {
  Credentials,
  ApiPortalEntry,
  ApiUser,
  PasswordResetInput,
  Session,
  SignInCodeInput,
  User,
} from "@/types/auth"
import type {
  ApiOrganisation,
  OrganisationAbilities,
} from "@/types/organisation"

const noAbilities: OrganisationAbilities = {
  manage_settings: false,
  manage_team: false,
  maintain_records: false,
  override: false,
  approve: false,
}

export function toUser(user: ApiUser): User {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    avatarUrl: user.avatar_url,
    emailVerifiedAt: user.email_verified_at,
    admin: user.is_system_admin,
    lastSignedInAt: user.last_signed_in_at,
    createdAt: user.created_at,
  }
}

type SignedIn = ApiResource<{ token: string }>

export const authQueries = {
  session: () =>
    queryOptions({
      queryKey: apiKey("/auth/session"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<
          ApiResource<{
            user: ApiUser
            organisations: ApiOrganisation[]
            portal: ApiPortalEntry[]
          }>
        >("/auth/session", { anonymous: true, signal })
        return {
          user: toUser(data.user),
          organisations: data.organisations.map((organisation) => ({
            id: organisation.id,
            name: organisation.name,
            logoUrl: organisation.logo_url,
            iconUrl: organisation.icon_url,
            role: organisation.role ?? "user",
            can: organisation.can ?? noAbilities,
            home: organisation.home ?? "/dashboard",
          })),
          portal: data.portal.map((entry) => ({
            id: entry.individual_id,
            firstName: entry.first_name,
            organisationId: entry.organisation.id,
            organisationName: entry.organisation.name,
          })),
        } satisfies Session
      },
      retry: false,
      staleTime: 5 * 60 * 1000,
    }),
}

export function useSignIn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: Credentials) => {
      return await apiFetch<SignedIn>("/auth/login", {
        method: "POST",
        body: credentials,
        anonymous: true,
      })
    },
    onSuccess: ({ data }) => {
      saveToken(data.token)
      queryClient.clear()
      notifySignedIn()
    },
  })
}

export function useRequestSignInCode() {
  return useMutation({
    mutationFn: async (input: { email: string }) => {
      const response = await apiFetch<{ challenge_id: string }>("/auth/code", {
        method: "POST",
        body: input,
        anonymous: true,
      })

      return { challengeId: response.challenge_id }
    },
  })
}

export function useSignInWithCode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SignInCodeInput) => {
      return await apiFetch<SignedIn>("/auth/code", {
        method: "PUT",
        body: { challenge_id: input.challengeId, code: input.code },
        anonymous: true,
      })
    },
    onSuccess: ({ data }) => {
      saveToken(data.token)
      queryClient.clear()
      notifySignedIn()
    },
  })
}

export function useSignOut() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await apiFetch<void>("/auth/logout", {
        method: "POST",
      })
    },
    onSuccess: () => {
      clearToken()
      notifySignedOut()
      queryClient.clear()
    },
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (input: { email: string }) => {
      const response = await apiFetch<{ challenge_id: string }>(
        "/auth/password-reset",
        { method: "POST", body: input, anonymous: true }
      )

      return { challengeId: response.challenge_id }
    },
  })
}

export function useResetPassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: PasswordResetInput) => {
      await apiFetch<void>("/auth/password-reset", {
        method: "PUT",
        body: {
          challenge_id: input.challengeId,
          code: input.code,
          password: input.password,
          password_confirmation: input.passwordConfirmation,
        },
        anonymous: true,
      })
    },
    onSuccess: () => {
      clearToken()
      notifySignedOut()
      queryClient.clear()
    },
  })
}
